/**
 * H1 Sprint 3 / I5 (parcial): codec aislado para `client_state` de Telnyx.
 *
 * Antes vivía dentro de `telnyxWebRTCStore.ts` (905 LOC). Aislarlo permite:
 *   - testearlo sin levantar el store
 *   - reusarlo desde otros módulos (cart_recovery_outcome_handler, etc.)
 *   - en futuro, mover el atob() pesado a Web Worker sin tocar el store
 *
 * Telnyx Call Control envía `client_state` (base64 JSON) en la pata al agente.
 * Aunque el SDK marque `direction: inbound` al WebRTC, este payload indica que
 * el llamado es callback al cliente (pedido web/COD), no una llamada entrante.
 */

const RECOGNIZED_KEYS = ['to_number', 'cod_order_id', 'ecommerce_order_id'] as const;
const NESTED_KEY_HINTS = ['client_state', 'clientState', 'custom_headers', 'variables'] as const;

/** Decodifica un string base64 (URL-safe o standard) y parsea como JSON. Devuelve null si no luce un payload Telnyx. */
function parseB64Json(rawIn: string): Record<string, unknown> | null {
  const raw = rawIn.trim();
  if (!raw || raw.length < 8) return null;
  try {
    const pad = '='.repeat((4 - (raw.length % 4)) % 4);
    const dec = atob(raw.replace(/-/g, '+').replace(/_/g, '/') + pad);
    const j = JSON.parse(dec) as unknown;
    if (!j || typeof j !== 'object' || Array.isArray(j)) return null;
    const o = j as Record<string, unknown>;
    if (RECOGNIZED_KEYS.some((k) => k in o)) return o;
    return null;
  } catch {
    return null;
  }
}

/**
 * Busca recursivamente el `client_state` en el objeto call que pasa el SDK.
 * El SDK a veces entierra el payload en objetos anidados (sin documentar).
 */
export function decodeTelnyxClientStateJson(call: Record<string, unknown>): Record<string, unknown> | null {
  const opt = (call.options ?? call.params) as Record<string, unknown> | undefined;
  if (opt && typeof opt === 'object') {
    const raw =
      (typeof opt.client_state === 'string' && opt.client_state.trim()) ||
      (typeof opt.clientState === 'string' && opt.clientState.trim()) ||
      '';
    const j = parseB64Json(raw);
    if (j) return j;
  }
  const topRaw =
    (typeof call.client_state === 'string' && call.client_state.trim()) ||
    (typeof call.clientState === 'string' && call.clientState.trim()) ||
    '';
  const top = parseB64Json(topRaw);
  if (top) return top;

  const seen = new WeakSet<object>();
  const walk = (node: unknown, depth: number): Record<string, unknown> | null => {
    if (depth > 12 || node == null) return null;
    if (typeof node === 'string') return parseB64Json(node);
    if (typeof node !== 'object') return null;
    if (seen.has(node as object)) return null;
    seen.add(node as object);
    const o = node as Record<string, unknown>;
    for (const key of NESTED_KEY_HINTS) {
      const v = o[key];
      if (typeof v === 'string') {
        const j = parseB64Json(v);
        if (j) return j;
      }
    }
    for (const v of Object.values(o)) {
      if (typeof v === 'string' && v.length > 16) {
        const j = parseB64Json(v);
        if (j) return j;
      }
      if (v && typeof v === 'object') {
        const j = walk(v, depth + 1);
        if (j) return j;
      }
    }
    return null;
  };
  return walk(call, 0);
}

/** Heurística: el call viene del flujo outbound del campaign (web/COD callback). */
export function callLooksLikeOutboundCampaign(call: Record<string, unknown>): boolean {
  const j = decodeTelnyxClientStateJson(call);
  if (!j) return false;
  return !!(j.to_number || j.cod_order_id || j.ecommerce_order_id);
}
